import { Component, inject, Injector, OnDestroy, OnInit, Signal } from '@angular/core';
import { MailboxTenantScopeService } from '@modules/common/services/mailbox-tenant-scope.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { Tab } from '@modules/common/types/tab';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { MailAppStateService } from '@modules/messages/services/mail-app-state.service';
import { MessageFolderListStateService } from '@modules/messages/services/message-folder-list-state.service';
import { MessageListStateService } from '@modules/messages/services/message-list-state.service';
import { type ListState } from '@modules/messages/types/list-state';
import { Message } from '@modules/messages/types/message';
import { MessageFoldersFilters } from '@modules/messages/types/message-folders-filters';
import { MessagesFilters } from '@modules/messages/types/messages-filters';
import { VirtualFolder } from '@modules/messages/types/virtual-folder';
import { isCreationAllowedInVirtualFolder } from '@modules/common/utils/virtual-folder';
import { StateKey } from '@modules/settings/types/state-key';

// Services
import { ModalService } from '@modules/modal/services/modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';

// RX
import { MessageFoldersService } from '@modules/messages/services/message-folders.service';
import { MessagesService } from '@modules/messages/services/messages.service';
import { MessageSummaryDto } from '@sharedlib/dtos/dto/mail-app/message.dto';
import { combineLatest, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-messages2-panel',
  templateUrl: './messages-panel.component.html',
  styleUrls: ['./messages-panel.component.less'],
  standalone: false,
  providers: [
    ...MessageListStateService.providers({
      stateKey: StateKey.messagesDPListState,
      withAdvancedSearch: true,
    }),
    ...MessageFolderListStateService.providers({
      stateKey: StateKey.messagesFoldersDPListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class MessagesPanelComponent implements OnInit, OnDestroy {
  public foldersFilters: MessageFoldersFilters;
  public messagesFilters: MessagesFilters;
  public contextMenuOpened: boolean;
  public selectedFilter: VirtualFolder;
  public listState: ListState;
  public tabs: Tab[] = [
    { title: 'Folders', value: 'folders' },
    { title: 'Messages', value: 'messages' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'messages';

  protected readonly popoverClose = new Subject<void>();
  private readonly alive = new Subject<void>();

  private readonly messageListStateService = inject(MessageListStateService, { self: true });
  private readonly messageFolderListStateService = inject(MessageFolderListStateService, { self: true });
  private readonly messageService = inject(MessagesService);
  private readonly messageFoldersService = inject(MessageFoldersService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  /** The panel's own Mailbox section: where its quick form creates (#2051). */
  protected readonly mailboxTenantScope = inject(MailboxTenantScopeService, { optional: true });

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  /** Location badges are shown on message rows only while an advanced search is applied. */
  protected readonly isAdvancedSearchApplied: Signal<boolean>;
  /**
   * Search-shaped: the shared query/markers, transient ids, and this app's form-only filters (From/To/Cc/Bcc)
   * — nothing else reports those, so this pill must. The date range and `stitched` are excluded on purpose:
   * they round-trip from the list's filter menu and light that menu's funnel pill instead.
   */
  protected readonly isSearchApplied: Signal<boolean>;

  /**
   * Derived pages (Flagged/Follow Up/Snoozed/Archive/Trash) offer no folder-creation form — DP-001.
   * Mail's other pages need no check: `getTabs()` pins them to the Messages tab, so the Folders tab —
   * and its form — is unreachable there, and the message form is already restricted to Inbox.
   */
  protected readonly creationAllowed: Signal<boolean>;

  constructor(
    private dpService: DynamicPanelService,
    private modalService: ModalService,
    private injector: Injector,
    private searchService: AdvancedSearchService,
    private stateService: MailAppStateService,
  ) {
    this.isAdvancedSearchApplied = toSignal(this.searchService.isAdvancedSearchApplied(Application.mail), {
      initialValue: false,
    });
    this.isSearchApplied = toSignal(this.searchService.isSearchApplied(Application.mail), { initialValue: false });
    this.creationAllowed = toSignal(
      this.stateService.getVirtualFolder().pipe(map((folder) => isCreationAllowedInVirtualFolder(folder))),
      { initialValue: true },
    );
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
      });

    combineLatest([
      this.messageFolderListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.foldersFilters = new MessageFoldersFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    combineLatest([
      this.messageListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        this.messagesFilters = new MessagesFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    this.stateService
      .getVirtualFolder()
      .pipe(takeUntil(this.alive))
      .subscribe((selectedFilter) => {
        this.selectedFilter = selectedFilter;
      });
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  handleNewMessage(message = new Message()) {
    this.messageService.composeMessage({ message, injector: this.injector });
  }

  protected openMessageInModal(message: MessageSummaryDto) {
    this.messageService
      .getItem(message.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((fullMessage) => {
        this.modalService.openFullForm(fullMessage, this.injector);
      });
  }

  protected openMessage(id: string) {
    this.messageService
      .getItem(id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((fullMessage) => {
        this.dpService.setFormItem(fullMessage);
      });
  }

  protected openFolder(id: string) {
    this.messageFoldersService
      .getItem(id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((fullFolder) => {
        this.dpService.setFormItem(fullFolder);
      });
  }

  selectFilter(folder: VirtualFolder): void {
    this.stateService.setVirtualFolder(folder);
  }

  protected handleSearch(): void {
    this.popoverClose.next();
  }

  protected clearSearch(): void {
    // Only the search terms: the per-app date/stitched filters belong to the filters menu and its pill.
    this.searchService.resetSearchTerms();
    // The transient "View Contacts" ids are part of those terms, so the mode they opened ends here —
    // at the owner, independently of whether the folder-menu directive is mounted to notice.
    this.dpService.clearIdsSearchMode();
  }

  protected handleCancel(): void {
    this.popoverClose.next();
  }

  /** "Add details" on the quick form: continue in the full form with what was typed. */
  protected handleNewMessageFolder(folder: Stitch): void {
    this.modalService.openFullForm(folder, this.injector);
  }
}
